<script setup lang="ts">
  import { onMounted } from 'vue';
  import { VueFinalModal, useModal } from 'vue-final-modal';
  import {
    ColoredButton,
    ConfirmCloseModal,
    IconImage,
    IconRadio,
    PositionUserSelector,
    SelectInput,
    UiModalHeader,
  } from '@/shared/ui';
  import { formatDateToPtBR } from '@/shared/lib';
  import { usePositionUserSelection } from '@/entities/User';
  import {
    TRAINING_CONTENT_EXTENSION_OPTIONS,
    TRAINING_CONTENT_TITLE_MAX_LENGTH,
    TRAINING_CONTENT_TYPE_OPTIONS,
  } from '@/entities/Training';
  import type { TrainingContentExtension, TrainingContentType } from '@/entities/Training';
  import { useCreateTrainingContent } from '../model/useCreateTrainingContent';
  import CreateTrainingContentSourceSection from './CreateTrainingContentSourceSection.vue';

  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import CheckIcon from '@/shared/assets/icons/CheckIcon.svg';

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'created'): void;
  }>();

  const selection = usePositionUserSelection({ mode: 'by-selected-positions' });
  const form = useCreateTrainingContent({
    selectedPositionIds: selection.selectedPositions,
    selectedMemberIds: selection.selectedUsers,
  });

  const { open: openConfirmClose, close: closeConfirmClose } = useModal({
    component: ConfirmCloseModal,
    attrs: {
      title: 'Tem certeza que deseja fechar a criação do conteúdo?',
      description: 'As mudanças realizadas não serão salvas.',
      onClose() {
        closeConfirmClose();
      },
      onConfirm() {
        closeConfirmClose();
        form.cancelUpload();
        emit('close');
      },
    },
  });

  const handleClose = () => {
    if (form.isDirty.value || form.isSubmitting.value) {
      openConfirmClose();
      return;
    }
    emit('close');
  };

  const handleSave = async () => {
    const created = await form.submit();
    if (created) {
      emit('created');
      emit('close');
    }
  };

  onMounted(async () => {
    await Promise.all([form.loadPositions(), selection.fetchPositionsAndUsers()]);
  });
</script>

<template>
  <VueFinalModal
    class="flex items-center justify-center"
    content-class="flex max-h-[90vh] w-[95vw] max-w-[1264px] flex-col overflow-y-auto rounded-2xl border border-border-color bg-white-01"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="false"
    :esc-to-close="false"
  >
    <div data-testid="create-training-content-modal">
      <UiModalHeader
        class="border-b border-border-color px-6 py-3 text-sm"
        title="Criar Conteúdo"
        text-color="gray-07"
        title-size="text-sm"
        @close="handleClose"
      />

      <div class="flex flex-col gap-2 border-b border-border-color px-6 py-8">
        <span class="text-sm font-semibold text-gray-07">Tipo de conteúdo:</span>
        <div class="flex gap-8">
          <IconRadio
            v-for="option in TRAINING_CONTENT_TYPE_OPTIONS"
            :key="option.value"
            :data-testid="`training-content-type-${option.value}`"
            :model-value="form.contentType.value ?? undefined"
            :value="option.value"
            :label="option.label"
            name="training-content-type"
            variant="small"
            @update:model-value="form.contentType.value = $event as TrainingContentType"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2 border-b border-border-color px-6 py-8">
        <span class="text-sm font-semibold text-gray-07">Extensão do conteúdo:</span>
        <div class="flex gap-8">
          <IconRadio
            v-for="option in TRAINING_CONTENT_EXTENSION_OPTIONS"
            :key="option.value"
            :data-testid="`training-content-extension-${option.value}`"
            :model-value="form.extension.value ?? undefined"
            :value="option.value"
            :label="option.label"
            name="training-content-extension"
            variant="small"
            @update:model-value="form.extension.value = $event as TrainingContentExtension"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1.5 border-b border-border-color px-6 py-8">
        <label
          for="training-content-title"
          class="text-sm font-semibold text-gray-07"
        >Título do conteúdo:</label>
        <input
          id="training-content-title"
          v-model="form.title.value"
          data-testid="training-content-title"
          type="text"
          placeholder="Escreva um título que represente o conteúdo"
          :maxlength="TRAINING_CONTENT_TITLE_MAX_LENGTH"
          class="w-full rounded-lg border border-border-color px-4 py-3 text-sm text-gray-07 focus:outline-none"
        >
      </div>

      <div class="grid grid-cols-[348px_348px_180px] gap-4 border-b border-border-color px-6 py-8">
        <div class="flex flex-col gap-1.5">
          <label
            for="training-content-responsible"
            class="text-sm font-semibold text-gray-07"
          >Responsável pelo upload:</label>
          <input
            id="training-content-responsible"
            data-testid="training-content-responsible"
            type="text"
            disabled
            :value="form.responsibleName.value"
            class="w-full rounded-lg border border-border-color bg-gray-02/20 px-4 py-2.5 text-sm text-gray-07"
          >
        </div>

        <div class="flex flex-col gap-1.5">
          <span
            id="training-content-position-label"
            class="text-sm font-semibold text-gray-07"
          >Cargo:</span>
          <SelectInput
            data-testid="training-content-position"
            :model-value="form.selectedPositionId.value ?? ''"
            :options="form.availablePositions.value"
            :disabled="form.hasSinglePosition.value || form.isLoadingPositions.value"
            label="Selecione o cargo"
            aria-labelledby="training-content-position-label"
            @update:model-value="form.selectedPositionId.value = Number($event)"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <label
            for="training-content-created-at"
            class="text-sm font-semibold text-gray-07"
          >Data criação:</label>
          <div class="relative">
            <IconImage
              :src="CalendarIcon"
              alt="Calendário"
              class="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2"
            />
            <input
              id="training-content-created-at"
              data-testid="training-content-created-at"
              type="text"
              disabled
              :value="formatDateToPtBR(form.createdDate.value)"
              class="w-full rounded-lg border border-border-color bg-gray-02/20 py-2.5 pr-4 pl-11 text-sm text-gray-07"
            >
          </div>
        </div>
      </div>

      <CreateTrainingContentSourceSection
        :file="form.file.value"
        :file-error="form.fileError.value"
        :url="form.url.value"
        :url-error="form.urlError.value"
        :upload-progress="form.uploadProgress.value"
        :upload-status="form.uploadStatus.value"
        :is-upload-disabled="form.isUploadDisabled.value"
        :is-url-disabled="form.isUrlDisabled.value"
        @select-file="form.selectFile"
        @remove-file="form.removeFile"
        @update:url="form.url.value = $event"
      />

      <div class="flex flex-col gap-3 border-b border-border-color px-6 py-8">
        <span class="text-sm font-semibold text-gray-07">
          Selecione os colaboradores que terão acesso ao conteúdo:
        </span>
        <PositionUserSelector
          :positions="selection.positions.value"
          :users="selection.users.value"
          :selected-positions="selection.selectedPositions.value"
          :selected-users="selection.selectedUsers.value"
          :positions-selected-count="selection.positionsSelectedCount.value"
          :users-selected-count="selection.usersSelectedCount.value"
          show-user-registration
          @update:selected-positions="selection.selectedPositions.value = $event"
          @update:selected-users="selection.selectedUsers.value = $event"
        />
        <p
          v-if="form.visibilityError.value"
          data-testid="training-content-visibility-error"
          class="text-sm text-red-16"
        >
          {{ form.visibilityError.value }}
        </p>
      </div>

      <div class="flex justify-end gap-6 px-6 py-8">
        <ColoredButton
          data-testid="training-content-cancel"
          outlined
          color="gray-07"
          border-color="border-color"
          text="Cancelar"
          @click="handleClose"
        />
        <ColoredButton
          data-testid="training-content-save"
          text="Salvar"
          :icon="CheckIcon"
          icon-alt="Salvar"
          :disabled="!form.canSave.value"
          :loading="form.isSubmitting.value"
          @click="handleSave"
        />
      </div>
    </div>
  </VueFinalModal>
</template>
